import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useEffect } from "react";
import { useAuth } from "./context/AuthContext";
import { usePushNotifications } from "./hooks/usePushNotifications";
import api from "./utils/api";

// Apply site-wide accent colour from admin settings on first load
const applySiteAccent = async () => {
  try {
    const res = await api.get('/site-settings');
    const color = res.data?.accent_color;
    if (color) document.documentElement.style.setProperty('--brand-accent', color);
  } catch {
    // Keep default #C5A52A from index.css
  }
};
applySiteAccent();

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

// Track page views in Matomo on every SPA route change
function MatomoPageView() {
  const { pathname, search } = useLocation();
  useEffect(() => {
    if (window._paq) {
      window._paq.push(['setCustomUrl', pathname + search]);
      window._paq.push(['setDocumentTitle', document.title]);
      window._paq.push(['trackPageView']);
    }
  }, [pathname, search]);
  return null;
}

// Pages
import Home from "./pages/Home";
import Login from "./pages/Login";
import Register from "./pages/Register";
import VerifyEmail from "./pages/VerifyEmail";
import Dashboard from "./pages/Dashboard";
import Account from "./pages/Account";
import CouncilTracker from "./pages/CouncilTracker";
import AdminPanel from "./pages/AdminPanel";
import AdminCouncillorView from "./pages/AdminCouncillorView";
import AdminCouncilVoteEntry from "./pages/AdminCouncilVoteEntry";
import Advertise from "./pages/Advertise";
import Realtors from "./pages/Realtors";
import RealtorSetup from "./pages/RealtorSetup";
import StandardAdSetup from "./pages/StandardAdSetup";
import ForgotPassword from "./pages/ForgotPassword";
import UserProfile from "./pages/UserProfile";
import EmailDesigner from "./pages/EmailDesigner";

// New Pages
import Submit from "./pages/Submit";
import Relay from "./pages/Relay";
import Takedown from "./pages/Takedown";
import Terms from "./pages/Terms";
import Badges from "./pages/Badges";
import BadgeLegend from "./pages/BadgeLegend";
import AdminBadgeCreator from "./pages/AdminBadgeCreator";
import AdminBadgeEditor from "./pages/AdminBadgeEditor";
import FoundersCorner from "./pages/FoundersCorner";
import PointsStore from "./pages/PointsStore";
import Leaderboard from "./pages/Leaderboard";
import EmbedBadge from "./pages/EmbedBadge";
import ClaimBadge from "./pages/ClaimBadge";

// Councillor Dashboard Pages
import WardPage from "./pages/WardPage";
import ReginaPage from "./pages/ReginaPage";
import CouncillorClaim from "./pages/CouncillorClaim";
import CouncillorDashboard from "./pages/CouncillorDashboard";
import CouncillorProfileEdit from "./pages/CouncillorProfileEdit";
import CouncillorEmailBlastCompose from "./pages/CouncillorEmailBlastCompose";
import NonProfitLanding from "./pages/NonProfitLanding";
import NonprofitTerms from "./pages/NonprofitTerms";
import PostPage from "./pages/PostPage";
import VaultPage from "./pages/VaultPage";
import VaultInboxPage from "./pages/VaultInboxPage";

// QCCA — Condo Community Suite
import QCCALanding from "./pages/QCCALanding";
import QCCAAdmin from "./pages/QCCAAdmin";
import QCCABoardroom from "./pages/QCCABoardroom";
import QCCABoardroomStub from "./pages/QCCABoardroomStub";
import QCCAVoteList from "./pages/QCCAVoteList";
import CondoWelcome from "./pages/CondoWelcome";
import QCCAVoteCreate from "./pages/QCCAVoteCreate";
import QCCAVoteDetail from "./pages/QCCAVoteDetail";
import QCCABallot from "./pages/QCCABallot";
import QCCAPublicResults from "./pages/QCCAPublicResults";
import QCCAClerkList from "./pages/QCCAClerkList";
import QCCAClerkNew from "./pages/QCCAClerkNew";
import QCCAClerkDetail from "./pages/QCCAClerkDetail";
import QCCAMinuteBook from "./pages/QCCAMinuteBook";
import QCCAMinuteBookDoc from "./pages/QCCAMinuteBookDoc";

// Protected Route
import ProtectedRoute from "./components/ProtectedRoute";

function PushInit() {
  const { user } = useAuth();
  usePushNotifications(user);
  return null;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <PushInit />
        <ScrollToTop />
        <MatomoPageView />
        <Routes>
          {/* Main Pages */}
          <Route path="/" element={<Home />} />
          <Route path="/submit" element={<Submit />} />
          <Route path="/relay" element={<Relay />} />
          <Route path="/council" element={<CouncilTracker />} />
          <Route path="/ledger" element={<CouncilTracker />} />
          <Route path="/badges" element={<Badges />} />
          <Route path="/badges/legend" element={<BadgeLegend />} />
          <Route path="/founders-corner" element={<FoundersCorner />} />
          
          {/* Auth Pages */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ForgotPassword />} />
          
          {/* User Pages */}
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/account" element={<Account />} />
          <Route path="/profile/:userId" element={<UserProfile />} />
          <Route path="/store" element={<PointsStore />} />
          <Route path="/leaderboard" element={<Navigate to="/badges?tab=rankings" replace />} />
          <Route path="/ranks" element={<Navigate to="/badges?tab=rankings" replace />} />
          
          {/* Admin */}
          <Route path="/admin" element={
            <ProtectedRoute requiredRole="admin" showAccessDenied={true}>
              <AdminPanel />
            </ProtectedRoute>
          } />
          <Route path="/admin/councillor/:councillorId" element={
            <ProtectedRoute requiredRole="admin" showAccessDenied={true}>
              <AdminCouncillorView />
            </ProtectedRoute>
          } />
          <Route path="/admin/council-votes" element={
            <ProtectedRoute requiredRole="admin" showAccessDenied={true}>
              <AdminCouncilVoteEntry />
            </ProtectedRoute>
          } />
          <Route path="/admin/badges/create" element={
            <ProtectedRoute requiredRole="admin" showAccessDenied={true}>
              <AdminBadgeCreator />
            </ProtectedRoute>
          } />
          <Route path="/admin/badges/edit/:badgeId" element={
            <ProtectedRoute requiredRole="admin" showAccessDenied={true}>
              <AdminBadgeEditor />
            </ProtectedRoute>
          } />
          <Route path="/admin/email-designer" element={
            <ProtectedRoute requiredRole="admin" showAccessDenied={true}>
              <EmailDesigner />
            </ProtectedRoute>
          } />
          
          {/* Councillor Pages */}
          <Route path="/ward-:wardNumber" element={<WardPage />} />
          <Route path="/ward/ward-:wardNumber" element={<WardPage />} />
          <Route path="/ward/:wardNumber" element={<WardPage />} />
          <Route path="/councillor/claim" element={<CouncillorClaim />} />
          <Route path="/councillor/dashboard" element={
            <ProtectedRoute 
              requiredBadges={['councillor', 'mayor']} 
              showAccessDenied={true}
              redirectTo="/"
            >
              <CouncillorDashboard />
            </ProtectedRoute>
          } />
          <Route path="/councillor/profile/edit" element={
            <ProtectedRoute 
              requiredBadges={['councillor', 'mayor']} 
              showAccessDenied={true}
            >
              <CouncillorProfileEdit />
            </ProtectedRoute>
          } />
          <Route path="/councillor/email-blast/new" element={
            <ProtectedRoute
              requiredBadges={['councillor', 'mayor']}
              showAccessDenied={true}
            >
              <CouncillorEmailBlastCompose />
            </ProtectedRoute>
          } />
          <Route path="/councillor/email-blast/:blastId/edit" element={
            <ProtectedRoute
              requiredBadges={['councillor', 'mayor']}
              showAccessDenied={true}
            >
              <CouncillorEmailBlastCompose />
            </ProtectedRoute>
          } />
          
          {/* Info Pages */}
          <Route path="/advertise" element={<Advertise />} />
          <Route path="/advertise/setup" element={<StandardAdSetup />} />
          <Route path="/realtors" element={<Realtors />} />
          <Route path="/realtors/setup" element={<RealtorSetup />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/terms-nonprofit" element={<NonprofitTerms />} />
          <Route path="/nonprofits" element={<NonProfitLanding />} />
          <Route path="/audit" element={
            <ProtectedRoute requiredRole="admin" showAccessDenied={true}>
              <Navigate to="/admin" replace />
            </ProtectedRoute>
          } />
          <Route path="/post/:postId" element={<PostPage />} />

          {/* ── Whistleblower Vault (isolated, no CYQR styles) ─────── */}
          <Route path="/vault" element={<VaultPage />} />
          <Route path="/vault/inbox" element={<VaultInboxPage />} />

          {/* ── QCCA Condo Community Suite ──────────────────────────── */}
          <Route path="/qcca" element={<QCCALanding />} />
          <Route path="/condo-welcome" element={<CondoWelcome />} />
          <Route path="/qcca/admin" element={<QCCAAdmin />} />
          <Route path="/qcca/boardroom" element={<QCCABoardroom />} />
          <Route path="/qcca/boardroom/votes" element={<QCCAVoteList />} />
          <Route path="/qcca/boardroom/votes/create" element={<QCCAVoteCreate />} />
          <Route path="/qcca/boardroom/votes/:voteId" element={<QCCAVoteDetail />} />
          <Route path="/qcca/boardroom/clerk" element={<QCCAClerkList />} />
          <Route path="/qcca/boardroom/clerk/new" element={<QCCAClerkNew />} />
          <Route path="/qcca/boardroom/clerk/:sessionId" element={<QCCAClerkDetail />} />
          <Route path="/qcca/boardroom/minutebook" element={<QCCAMinuteBook />} />
          <Route path="/qcca/boardroom/minutebook/:docId" element={<QCCAMinuteBookDoc />} />
          <Route path="/qcca/boardroom/:stub" element={<QCCABoardroomStub />} />
          <Route path="/vote/:token" element={<QCCABallot />} />
          <Route path="/results/:voteId" element={<QCCAPublicResults />} />
          <Route path="/takedown" element={<Takedown />} />
          <Route path="/regina" element={<ReginaPage />} />
          <Route path="/ward/mayor" element={<ReginaPage />} />
          <Route path="/embed/badge/:code" element={<EmbedBadge />} />
          <Route path="/claim-badge/:code" element={<ClaimBadge />} />
        </Routes>
      </BrowserRouter>
      
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
      />
    </AuthProvider>
  );
}

export default App;
